import React, { useContext, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

type FooterLayoutId = 'classic' | 'simple' | 'corporate' | 'minimal' | 'custom-grid';
type Align = 'left' | 'center' | 'right';
type FieldKey =
  | 'companyName'
  | 'addressLine1'
  | 'addressLine2'
  | 'phone'
  | 'fax'
  | 'email'
  | 'website'
  | 'legalInfo'
  | 'fontColor'
  | 'dense'
  | 'showPageNumbers';

type FooterData = {
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  legalInfo: string;
  fontColor: string;
  showPageNumbers: boolean;
  dense: boolean;
};

type FooterLayoutDefinition = {
  id: FooterLayoutId;
  name: string;
  description: string;
  preview: React.ReactNode;
};

type DataFieldKey =
  | 'companyName'
  | 'addressLine1'
  | 'addressLine2'
  | 'phone'
  | 'fax'
  | 'email'
  | 'website'
  | 'legalInfo';

const initialData: FooterData = {
  companyName: 'V-Chainer Sp. z o.o. z siedzibą we Wrocławiu',
  addressLine1: 'ul. Legnicka 48',
  addressLine2: '54-203 Wrocław',
  phone: '+48 71 123 45 67',
  fax: '+48 71 123 45 68',
  email: 'contact@v-chainer.com',
  website: 'www.v-chainer.com',
  legalInfo:
    'Sąd Rejonowy dla Wrocławia-Fabrycznej we Wrocławiu, VI Wydział Gospodarczy KRS 0000987654, NIP 897-189-45-21, REGON 521234567, Kapitał zakładowy 50 000 PLN.',
  fontColor: '#355398',
  showPageNumbers: true,
  dense: false,
};

const layoutDefinitions: FooterLayoutDefinition[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'P1: firma, P2/3: adres, P4/5/6/7: kontakt, P8: dane formalne',
    preview: (
      <MiniGrid columns="42px 1.2fr 1fr 1fr 42px" rows="14px 14px 14px 14px">
        <MiniFixedV rowSpan={4} />
        <MiniCell colSpan={3} label="Firma" strong />
        <MiniFixedPage rowSpan={4} />
        <MiniCell label="Adr 1" />
        <MiniCell label="Tel" />
        <MiniCell label="Email" />
        <MiniCell label="Adr 2" />
        <MiniCell label="Fax" />
        <MiniCell label="WWW" />
        <MiniCell colSpan={3} label="Formalne" />
      </MiniGrid>
    ),
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'Firma u góry, niżej adres i kontakt, po prawej stała kolumna danych formalnych',
    preview: (
      <MiniGrid columns="15fr 12fr 9fr 12fr 27fr 15fr" rows="14px 14px 14px">
        <MiniFixedV rowSpan={3} />
        <MiniCell colSpan={4} label="Firma" strong />
        <MiniFixedPage rowSpan={3} />
        <MiniCell label="Adr 1" />
        <MiniCell label="Tel" />
        <MiniCell label="Email" />
        <MiniCell rowSpan={2} label="Formalne" />
        <MiniCell label="Adr 2" />
        <MiniCell label="Fax" />
        <MiniCell label="WWW" />
      </MiniGrid>
    ),
  },
  {
    id: 'simple',
    name: 'Simple',
    description: 'Dwa wiersze: pierwszy pogrubiony, drugi zwykły',
    preview: (
      <MiniGrid columns="42px 1fr 42px" rows="16px 16px">
        <MiniFixedV rowSpan={2} />
        <MiniCell label="Wiersz 1" strong />
        <MiniFixedPage rowSpan={2} />
        <MiniCell label="Wiersz 2" />
      </MiniGrid>
    ),
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'P1+2/3: blok firmowy, P4/6/7: linia kontaktu (układ oszczędny)',
    preview: (
      <MiniGrid columns="42px 1.2fr 1fr 42px" rows="22px">
        <MiniFixedV />
        <MiniCell label="Kol 1" strong />
        <MiniCell label="Kol 2" />
        <MiniFixedPage />
      </MiniGrid>
    ),
  },
  {
    id: 'custom-grid',
    name: 'Custom Grid',
    description: 'Konfigurowany jest tylko środkowy obszar. Lewa i prawa kolumna pozostają stałe.',
    preview: (
      <MiniGrid columns="42px 1fr 1fr 1fr 1fr 42px" rows="14px 14px 14px">
        <MiniFixedV rowSpan={3} />
        <MiniCell label="A1" />
        <MiniCell label="A2" />
        <MiniCell label="A3" />
        <MiniCell label="A4" />
        <MiniFixedPage rowSpan={3} />
        <MiniCell label="B1" />
        <MiniCell label="B2" />
        <MiniCell label="B3" />
        <MiniCell label="B4" />
        <MiniCell label="C1" />
        <MiniCell label="C2" />
        <MiniCell label="C3" />
        <MiniCell label="C4" />
      </MiniGrid>
    ),
  },
];

const fieldLabelsByLayout: Record<FooterLayoutId, Record<DataFieldKey, string>> = {
  classic: {
    companyName: 'Nazwa firmy',
    addressLine1: 'Adres - linia 1',
    addressLine2: 'Adres - linia 2',
    phone: 'Telefon',
    fax: 'Fax',
    email: 'Email',
    website: 'WWW',
    legalInfo: 'Dane formalne',
  },
  simple: {
    companyName: 'Wiersz 1 - tekst pogrubiony',
    addressLine1: 'Wiersz 2',
    addressLine2: 'Wiersz 2',
    phone: 'Wiersz 2',
    fax: 'Wiersz 2',
    email: 'Wiersz 2',
    website: 'Wiersz 2',
    legalInfo: 'Wiersz 2',
  },
  corporate: {
    companyName: 'Nazwa firmy',
    addressLine1: 'Adres - linia 1',
    addressLine2: 'Adres - linia 2',
    phone: 'Telefon',
    fax: 'Fax',
    email: 'Email',
    website: 'WWW',
    legalInfo: 'Dane formalne',
  },
  minimal: {
    companyName: 'Kol 1',
    addressLine1: 'Kol 2',
    addressLine2: 'Kol 2',
    phone: 'Kol 2',
    fax: 'Kol 2',
    email: 'Kol 2',
    website: 'Kol 2',
    legalInfo: 'Kol 2',
  },
  'custom-grid': {
    companyName: 'Pole 1',
    addressLine1: 'Pole 2',
    addressLine2: 'Pole 3',
    phone: 'Pole 4',
    fax: 'Pole 5',
    email: 'Pole 6',
    website: 'Pole 7',
    legalInfo: 'Pole 8',
  },
};

function MiniGrid({
  columns,
  rows,
  children,
}: {
  columns: string;
  rows: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        gridTemplateRows: rows,
        gap: 0.35,
        p: 0.75,
        bgcolor: '#fafafa',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        minHeight: 62,
      }}
    >
      {children}
    </Box>
  );
}

function MiniCell({
  label,
  strong,
  align = 'left',
  colSpan = 1,
  rowSpan = 1,
}: {
  label: string;
  strong?: boolean;
  align?: Align;
  colSpan?: number;
  rowSpan?: number;
}) {
  return (
    <Box
      sx={{
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        px: 0.5,
        py: 0.15,
        display: 'flex',
        alignItems: 'center',
        justifyContent:
          align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'flex-end',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: strong ? 700 : 400,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MiniFixedV({ rowSpan = 1 }: { rowSpan?: number }) {
  return <MiniCell rowSpan={rowSpan} label="V" align="center" strong />;
}

function MiniFixedPage({ rowSpan = 1 }: { rowSpan?: number }) {
  return <MiniCell rowSpan={rowSpan} label="1/N" align="center" strong />;
}

function SectionHeader({
  title,
  fullWidth = false,
}: {
  title: string;
  fullWidth?: boolean;
}) {
  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'auto',
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: 'grey.100',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
  );
}

function getTextSx(data: FooterData, strong = false, align: Align = 'left') {
  return {
    color: data.fontColor,
    fontSize: data.dense ? 10 : 11,
    lineHeight: 1.2,
    textAlign: align,
    fontWeight: strong ? 700 : 400,
    wordBreak: 'break-word' as const,
  };
}

function getAreaSx(active: boolean) {
  return {
    border: '1px dashed',
    borderColor: active ? 'primary.main' : 'transparent',
    bgcolor: active ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
    borderRadius: 1,
    px: 0.35,
    py: 0.15,
    transition: 'all .15s ease',
    cursor: 'pointer',
    minHeight: 18,
  };
}

function AreaLabel({ label }: { label: string }) {
  const showLabels = useContext(ShowAreaLabelsContext);
  if (!showLabels) {
    return null;
  }

  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        color: 'text.disabled',
        fontSize: 10,
        lineHeight: 1,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
  );
}

const ShowAreaLabelsContext = React.createContext(true);

function PreviewPage({
  data,
  layout,
  customColumns,
  customRows,
  activeField,
  onSelectField,
}: {
  data: FooterData;
  layout: FooterLayoutId;
  customColumns: number;
  customRows: number;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  return (
    <Paper
      elevation={3}
      sx={{
        width: '100%',
        maxWidth: 860,
        minHeight: 360,
        mx: 'auto',
        p: 1.5,
        borderRadius: 3,
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 700 }}>
          Podgląd dokumentu
        </Typography>
        <Box
          sx={{
            mt: 1.25,
            minHeight: 110,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1,
            color: 'text.secondary',
          }}
        >
          Przykładowa treść dokumentu...
        </Box>
      </Box>

      <Box sx={{ pt: 2 }}>
        <Divider sx={{ borderColor: data.fontColor, mb: 0.75 }} />
        <ShowAreaLabelsContext.Provider value={false}>
          {layout === 'classic' && (
            <ClassicFooter data={data} activeField={activeField} onSelectField={onSelectField} />
          )}
          {layout === 'simple' && (
            <SimpleFooter data={data} activeField={activeField} onSelectField={onSelectField} />
          )}
          {layout === 'corporate' && (
            <CorporateFooter data={data} activeField={activeField} onSelectField={onSelectField} />
          )}
          {layout === 'minimal' && (
            <MinimalFooter data={data} activeField={activeField} onSelectField={onSelectField} />
          )}
          {layout === 'custom-grid' && (
            <CustomGridFooter
              data={data}
              columns={customColumns}
              rows={customRows}
              activeField={activeField}
              onSelectField={onSelectField}
            />
          )}
        </ShowAreaLabelsContext.Provider>
      </Box>
    </Paper>
  );
}

function FixedLeftPrintBlock({ data }: { data: FooterData }) {
  return (
    <Box>
      <Typography sx={getTextSx(data)}>Wydruk</Typography>
      <Typography sx={getTextSx(data)}>© v-chainer.com</Typography>
    </Box>
  );
}

function FixedRightPageBlock({ data, activeField, onSelectField }: {
  data: FooterData;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  return (
    <Box sx={getAreaSx(activeField === 'showPageNumbers')} onClick={() => onSelectField('showPageNumbers')}>
      <AreaLabel label="Pole numeracji" />
      <Typography sx={getTextSx(data, false, 'right')}>
        {data.showPageNumbers ? '1 / 12' : 'Ukryta'}
      </Typography>
    </Box>
  );
}

function ClassicFooter({
  data,
  activeField,
  onSelectField,
}: {
  data: FooterData;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '90px 1.2fr 1fr 1fr 90px',
        gridTemplateRows: 'auto auto auto auto',
        gap: 0.5,
      }}
    >
      <Box sx={{ gridRow: '1 / span 4', pr: 1 }}>
        <FixedLeftPrintBlock data={data} />
      </Box>

      <Box
        sx={{ gridColumn: '2 / span 3', ...getAreaSx(activeField === 'companyName') }}
        onClick={() => onSelectField('companyName')}
      >
        <AreaLabel label="Pole 1" />
        <Typography sx={getTextSx(data, true)}>{data.companyName}</Typography>
      </Box>

      <Box sx={{ gridColumn: '5 / span 1', gridRow: '1 / span 4' }}>
        <FixedRightPageBlock data={data} activeField={activeField} onSelectField={onSelectField} />
      </Box>

      <Box sx={getAreaSx(activeField === 'addressLine1')} onClick={() => onSelectField('addressLine1')}>
        <AreaLabel label="Pole 2" />
        <Typography sx={getTextSx(data)}>{data.addressLine1}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'phone')} onClick={() => onSelectField('phone')}>
        <AreaLabel label="Pole 4" />
        <Typography sx={getTextSx(data)}>T: {data.phone}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'email')} onClick={() => onSelectField('email')}>
        <AreaLabel label="Pole 6" />
        <Typography sx={getTextSx(data)}>{data.email}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'addressLine2')} onClick={() => onSelectField('addressLine2')}>
        <AreaLabel label="Pole 3" />
        <Typography sx={getTextSx(data)}>{data.addressLine2}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'fax')} onClick={() => onSelectField('fax')}>
        <AreaLabel label="Pole 5" />
        <Typography sx={getTextSx(data)}>F: {data.fax}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'website')} onClick={() => onSelectField('website')}>
        <AreaLabel label="Pole 7" />
        <Typography sx={getTextSx(data)}>{data.website}</Typography>
      </Box>

      <Box
        sx={{ gridColumn: '2 / span 3', ...getAreaSx(activeField === 'legalInfo') }}
        onClick={() => onSelectField('legalInfo')}
      >
        <AreaLabel label="Pole 8" />
        <Typography sx={getTextSx(data)}>{data.legalInfo}</Typography>
      </Box>
    </Box>
  );
}

function SimpleFooter({
  data,
  activeField,
  onSelectField,
}: {
  data: FooterData;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr 90px',
        gridTemplateRows: 'auto auto',
        gap: 0.5,
      }}
    >
      <Box sx={{ gridRow: '1 / span 2', pr: 1 }}>
        <FixedLeftPrintBlock data={data} />
      </Box>

      <Box
        sx={getAreaSx(activeField === 'companyName')}
        onClick={() => onSelectField('companyName')}
      >
        <AreaLabel label="Pole 1" />
        <Typography sx={getTextSx(data, true)}>{data.companyName}</Typography>
      </Box>

      <Box sx={{ gridColumn: '3 / span 1', gridRow: '1 / span 2' }}>
        <FixedRightPageBlock data={data} activeField={activeField} onSelectField={onSelectField} />
      </Box>

      <Box sx={getAreaSx(activeField === 'legalInfo')} onClick={() => onSelectField('legalInfo')}>
        <AreaLabel label="Wiersz 2" />
        <Typography sx={getTextSx(data)}>{data.legalInfo}</Typography>
      </Box>
    </Box>
  );
}

function CorporateFooter({
  data,
  activeField,
  onSelectField,
}: {
  data: FooterData;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '90px 1.2fr 1fr 1fr 1.15fr 90px',
        gridTemplateRows: 'auto auto auto',
        gap: 0.5,
      }}
    >
      <Box sx={{ gridRow: '1 / span 3', pr: 1 }}>
        <FixedLeftPrintBlock data={data} />
      </Box>

      <Box
        sx={{ gridColumn: '2 / span 4', ...getAreaSx(activeField === 'companyName') }}
        onClick={() => onSelectField('companyName')}
      >
        <AreaLabel label="Pole 1" />
        <Typography sx={getTextSx(data, true)}>{data.companyName}</Typography>
      </Box>

      <Box sx={{ gridColumn: '6 / span 1', gridRow: '1 / span 3' }}>
        <FixedRightPageBlock data={data} activeField={activeField} onSelectField={onSelectField} />
      </Box>

      <Box sx={getAreaSx(activeField === 'addressLine1')} onClick={() => onSelectField('addressLine1')}>
        <AreaLabel label="Pole 2" />
        <Typography sx={getTextSx(data)}>{data.addressLine1}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'phone')} onClick={() => onSelectField('phone')}>
        <AreaLabel label="Pole 4" />
        <Typography sx={getTextSx(data)}>{data.phone}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'email')} onClick={() => onSelectField('email')}>
        <AreaLabel label="Pole 6" />
        <Typography sx={getTextSx(data)}>{data.email}</Typography>
      </Box>
      <Box
        sx={{ gridColumn: '5 / span 1', gridRow: '2 / span 2', ...getAreaSx(activeField === 'legalInfo') }}
        onClick={() => onSelectField('legalInfo')}
      >
        <AreaLabel label="Pole 8" />
        <Typography sx={getTextSx(data)}>{data.legalInfo}</Typography>
      </Box>

      <Box sx={getAreaSx(activeField === 'addressLine2')} onClick={() => onSelectField('addressLine2')}>
        <AreaLabel label="Pole 3" />
        <Typography sx={getTextSx(data)}>{data.addressLine2}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'fax')} onClick={() => onSelectField('fax')}>
        <AreaLabel label="Pole 5" />
        <Typography sx={getTextSx(data)}>F: {data.fax}</Typography>
      </Box>
      <Box sx={getAreaSx(activeField === 'website')} onClick={() => onSelectField('website')}>
        <AreaLabel label="Pole 7" />
        <Typography sx={getTextSx(data)}>{data.website}</Typography>
      </Box>
    </Box>
  );
}

function MinimalFooter({
  data,
  activeField,
  onSelectField,
}: {
  data: FooterData;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '90px 1.6fr 1.1fr 90px',
        gap: 1,
        alignItems: 'start',
      }}
    >
      <FixedLeftPrintBlock data={data} />

      <Box sx={getAreaSx(activeField === 'companyName')} onClick={() => onSelectField('companyName')}>
        <AreaLabel label="Kol 1" />
        <Typography sx={getTextSx(data, true)}>{data.companyName}</Typography>
      </Box>

      <Box sx={getAreaSx(activeField === 'legalInfo')} onClick={() => onSelectField('legalInfo')}>
        <AreaLabel label="Kol 2" />
        <Typography sx={getTextSx(data)}>{data.legalInfo}</Typography>
      </Box>

      <FixedRightPageBlock data={data} activeField={activeField} onSelectField={onSelectField} />
    </Box>
  );
}

function CustomGridFooter({
  data,
  columns,
  rows,
  activeField,
  onSelectField,
}: {
  data: FooterData;
  columns: number;
  rows: number;
  activeField: FieldKey | null;
  onSelectField: (field: FieldKey) => void;
}) {
  const cells = useMemo(
    () => [
      { field: 'companyName' as FieldKey, label: 'Pole 1', value: data.companyName },
      { field: 'addressLine1' as FieldKey, label: 'Pole 2', value: data.addressLine1 },
      { field: 'addressLine2' as FieldKey, label: 'Pole 3', value: data.addressLine2 },
      { field: 'phone' as FieldKey, label: 'Pole 4', value: `T: ${data.phone}` },
      { field: 'fax' as FieldKey, label: 'Pole 5', value: `F: ${data.fax}` },
      { field: 'email' as FieldKey, label: 'Pole 6', value: data.email },
      { field: 'website' as FieldKey, label: 'Pole 7', value: data.website },
      { field: 'legalInfo' as FieldKey, label: 'Pole 8', value: data.legalInfo },
    ],
    [data]
  );

  const total = columns * rows;
  const rendered = Array.from({ length: total }, (_, i) => cells[i] ?? null);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `90px repeat(${columns}, minmax(0, 1fr)) 90px`,
        gap: 0.75,
        alignItems: 'stretch',
      }}
    >
      <Box sx={{ gridRow: `1 / span ${rows}`, pr: 1 }}>
        <FixedLeftPrintBlock data={data} />
      </Box>

      <Box
        sx={{
          gridColumn: `2 / span ${columns}`,
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, auto)`,
          gap: 0.75,
        }}
      >
        {rendered.map((cell, index) => (
          <Box
            key={index}
            sx={getAreaSx(cell ? activeField === cell.field : false)}
            onClick={() => cell && onSelectField(cell.field)}
          >
            <AreaLabel label={cell?.label ?? 'Puste pole'} />
            <Typography sx={getTextSx(data)}>{cell?.value ?? 'Brak danych'}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ gridColumn: `${columns + 2}`, gridRow: `1 / span ${rows}` }}>
        <FixedRightPageBlock data={data} activeField={activeField} onSelectField={onSelectField} />
      </Box>
    </Box>
  );
}

export default function FooterDesignerMockup() {
  const [layout, setLayout] = useState<FooterLayoutId>('classic');
  const [data, setData] = useState<FooterData>(initialData);
  const [customColumns, setCustomColumns] = useState(4);
  const [customRows, setCustomRows] = useState(3);
  const [activeField, setActiveField] = useState<FieldKey | null>('companyName');
  const lastPublishedBy = 'Anna Kowalska';
  const lastPublishedAt = '21.03.2026, 14:32';

  const selectedLayout = layoutDefinitions.find((x) => x.id === layout)!;
  const selectedFieldLabels = fieldLabelsByLayout[layout];

  const companyNameRef = useRef<HTMLInputElement | null>(null);
  const addressLine1Ref = useRef<HTMLInputElement | null>(null);
  const addressLine2Ref = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const faxRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const websiteRef = useRef<HTMLInputElement | null>(null);
  const legalInfoRef = useRef<HTMLInputElement | null>(null);
  const fontColorRef = useRef<HTMLInputElement | null>(null);

  const fieldRefs: Partial<Record<FieldKey, React.RefObject<HTMLInputElement | null>>> = {
    companyName: companyNameRef,
    addressLine1: addressLine1Ref,
    addressLine2: addressLine2Ref,
    phone: phoneRef,
    fax: faxRef,
    email: emailRef,
    website: websiteRef,
    legalInfo: legalInfoRef,
    fontColor: fontColorRef,
  };

  const updateField = <K extends keyof FooterData>(key: K, value: FooterData[K]) => {
    setData((prev) => ({ ...prev, [key]: value }));
    if (key === 'fontColor' || key === 'dense' || key === 'showPageNumbers') {
      setActiveField(key as FieldKey);
    } else {
      setActiveField(key as FieldKey);
    }
  };

  const focusField = (field: FieldKey) => {
    setActiveField(field);
    const ref = fieldRefs[field];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      ref.current.focus();
    }
  };

  const commonInputProps = (field: FieldKey) => ({
    onFocus: () => setActiveField(field),
    inputRef: fieldRefs[field],
  });

  const renderDataField = (field: DataFieldKey, options?: { multiline?: boolean; minRows?: number }) => (
    <TextField
      label={selectedFieldLabels[field]}
      size="small"
      value={data[field]}
      onChange={(e) => updateField(field, e.target.value)}
      fullWidth
      multiline={options?.multiline}
      minRows={options?.minRows}
      {...commonInputProps(field)}
    />
  );

  const renderDataFields = () => {
    if (layout === 'classic' || layout === 'corporate') {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 1fr',
            gap: 1,
          }}
        >
          <Box sx={{ gridColumn: '1 / span 3' }}>{renderDataField('companyName')}</Box>
          {renderDataField('addressLine1')}
          {renderDataField('phone')}
          {renderDataField('email')}
          {renderDataField('addressLine2')}
          {renderDataField('fax')}
          {renderDataField('website')}
          <Box sx={{ gridColumn: '1 / span 3' }}>{renderDataField('legalInfo', { multiline: true, minRows: 2 })}</Box>
        </Box>
      );
    }

    if (layout === 'simple') {
      return (
        <Stack spacing={1}>
          {renderDataField('companyName')}
          {renderDataField('legalInfo', { multiline: true, minRows: 2 })}
        </Stack>
      );
    }

    if (layout === 'minimal') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            {renderDataField('companyName', { multiline: true, minRows: 2 })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderDataField('legalInfo', { multiline: true, minRows: 2 })}
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          {renderDataField('companyName')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderDataField('addressLine1')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderDataField('addressLine2')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderDataField('phone')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderDataField('fax')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderDataField('email')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderDataField('website')}
        </Grid>
        <Grid item xs={12}>
          {renderDataField('legalInfo', { multiline: true, minRows: 2 })}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Grid container spacing={1} alignItems="stretch">
        <Grid item xs={12}>
          <Paper sx={{ p: 1, borderRadius: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <SectionHeader title="1. Szablon" fullWidth />
            </Stack>

            <Grid container spacing={0.75}>
              {layoutDefinitions.filter((item) => item.id !== 'custom-grid').map((item) => {
                const active = item.id === layout;
                return (
                  <Grid item xs={12} sm={6} md={3} key={item.id} sx={{ display: 'flex' }}>
                    <Card
                      variant={active ? 'elevation' : 'outlined'}
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: active ? 'success.main' : 'divider',
                        boxShadow: active ? 2 : 0,
                      }}
                    >
                      <CardActionArea onClick={() => setLayout(item.id)} sx={{ height: '100%' }}>
                        <CardContent sx={{ p: 0.75, height: '100%' }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {item.name}
                            </Typography>
                            {active && <Chip size="small" label="Wybrany" color="success" />}
                          </Stack>
                          {item.preview}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.25, borderRadius: 3, width: '100%', height: '100%' }}>
            <Stack spacing={1.25}>
              <SectionHeader title="2. Dane" />

              {renderDataFields()}

              <Divider />

              <SectionHeader title="3. Styl" />

              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Kolor"
                    type="color"
                    size="small"
                    value={data.fontColor}
                    onChange={(e) => updateField('fontColor', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    {...commonInputProps('fontColor')}
                  />
                </Grid>
              </Grid>

              {layout === 'custom-grid' && (
                <Accordion disableGutters>
                  <Box sx={{ px: 1.5, py: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      Ustawienia siatki
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Liczba kolumn</InputLabel>
                        <Select
                          label="Liczba kolumn"
                          value={customColumns}
                          onChange={(e) => setCustomColumns(Number(e.target.value))}
                        >
                          {[2, 3, 4, 5].map((n) => (
                            <MenuItem key={n} value={n}>
                              {n}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <InputLabel>Liczba wierszy</InputLabel>
                        <Select
                          label="Liczba wierszy"
                          value={customRows}
                          onChange={(e) => setCustomRows(Number(e.target.value))}
                        >
                          {[1, 2, 3].map((n) => (
                            <MenuItem key={n} value={n}>
                              {n}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                </Accordion>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7} sx={{ display: 'flex' }}>
          <PreviewPage
            data={data}
            layout={layout}
            customColumns={customColumns}
            customRows={customRows}
            activeField={activeField}
            onSelectField={focusField}
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 1.25, borderRadius: 3 }}>
            <SectionHeader title="4. Publikacja" fullWidth />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mt: 1.25 }}
            >
              <Stack direction="row" spacing={1}>
                <Button variant="outlined">
                  Zapisz
                </Button>
                <Button variant="contained" color="success">
                  Opublikuj
                </Button>
              </Stack>

              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Ostatnia publikacja:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {lastPublishedBy}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {lastPublishedAt}
                </Typography>
              </Box>

              <Button variant="text" sx={{ ml: { sm: 'auto' }, whiteSpace: 'nowrap' }}>
                Podgląd historii wersji
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
